import { isDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { FormField } from '@/App/tiles/ForwardAccumulator/parts/RightPanel/common/FormField.tsx';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import {
  selectForwardAccumulatorCrossCurrencyData,
  selectForwardAccumulatorFixingReference1Data,
  selectForwardAccumulatorProperty,
} from '@/store/forwardAccumulator/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';

const FixingReference1 = () => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { fixingReference1, fixingReference1List, fixingReference1Default } = useAppSelector((state) =>
    selectForwardAccumulatorFixingReference1Data(state, tileId),
  );
  const { crossCurrency } = useAppSelector((state) => selectForwardAccumulatorCrossCurrencyData(state, tileId));

  const currencyPair = useAppSelector((state) => selectForwardAccumulatorProperty(state, tileId, 'currencyPair'));
  const [currency1, currency2] = currencyPair ? currencyPair.split('/') : [undefined, undefined];
  const isSameReferenceAsDefault = fixingReference1 === fixingReference1Default;

  const currencyPairDisplayed =
    isDefined(crossCurrency) && crossCurrency !== '' && crossCurrency !== currency2 && crossCurrency !== currency1
      ? `${currency1}/${crossCurrency}`
      : currencyPair;

  const handleFixingReference1 = (event: React.ChangeEvent<HTMLSelectElement>) => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Fixing reference 1 changed', event.target.value);

    return check({
      fixingReference1: event.target.value,
    });
  };

  return (
    <FormField
      formattedMessageId="forwardAccumulator.form.fixingReference"
      formattedMessageValues={{ currencyPair: currencyPairDisplayed ?? '' }}
      extra={
        !isSameReferenceAsDefault ? (
          <Tooltip
            position="right"
            contents={
              <FormattedMessage
                id="forwardAccumulator.form.fixingReference.info"
                values={{ defaultFixingReference: fixingReference1Default, br: <br /> }}
              />
            }
          >
            <div className="badge badge-dismissible">
              <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
            </div>
          </Tooltip>
        ) : undefined
      }
    >
      <Form.Select
        value={fixingReference1 ?? fixingReference1Default}
        onChange={handleFixingReference1}
        className={`${!isSameReferenceAsDefault ? 'text-warning fw-bold' : ''}`}
        size="sm"
        data-e2e="fixing-reference1-selector"
      >
        {fixingReference1List?.map((ref) => (
          <option className="text-primary" key={ref} value={ref}>
            {ref}
          </option>
        ))}
      </Form.Select>
    </FormField>
  );
};

export default FixingReference1;
